import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import { toRaw } from "vue";
import API from "@/api/API";
import { viewerColors } from "@/utils/viewer-colors";
import {
  COORDINTATE_LINE_COLOR_X,
  COORDINTATE_LINE_COLOR_Y,
  RENDERING_ORDER,
} from "@/constants";
import { ElMessage } from "element-plus";

export const createMeasurementMarker = function (referencePoint) {
  const wrapper = this.createReactivePoint(referencePoint, viewerColors.POINT_COLOR);

  return wrapper;
};

export const createCoordinateMeasurementLine = function (
  firstPoint,
  secondPoint,
  color,
  vertical
) {
  const lineGeometry = new THREE.BufferGeometry().setFromPoints([
    firstPoint,
    secondPoint,
  ]);

  const lineMaterial = new THREE.LineBasicMaterial({
    color: color,
    transparent: true,
  });

  const line = new THREE.Line(lineGeometry, lineMaterial);

  line.material.depthTest = false;
  line.renderOrder = RENDERING_ORDER.AUXILIARY_LINE;

  const distance = secondPoint.distanceTo(firstPoint);
  var div = document.createElement("div");
  div.className = "measurementLabelDetail";

  var span = document.createElement("span");
  span.textContent = `${this.$n(distance, "decimal")}`;

  var image = document.createElement("img");
  image.src = `/assets/model/${
    vertical ? "vertical_axis" : "horizontal_axis"
  }.svg`;
  image.className = "q-mr-xs";

  div.appendChild(image);
  div.appendChild(span);

  let label = new CSS2DObject(div);
  label.position.lerpVectors(secondPoint, firstPoint, 0.5);
  label.layers.set(0);

  return {
    line,
    label,
    distance,
  };
};

export const updateCoordinateMeasurementLine = function (
  line,
  label,
  firstPoint,
  secondPoint
) {
  line.geometry.setFromPoints([firstPoint, secondPoint]);
  line.computeLineDistances();
  const distance = secondPoint.distanceTo(firstPoint);
  let div = label.element;
  let span = document.createElement("span");
  span.textContent = `${this.$n(distance, "decimal")}`;
  div.replaceChild(span, div.lastChild);
  label.position.lerpVectors(secondPoint, firstPoint, 0.5);
};

export const addMarker = async function (event) {
  event.preventDefault();

  if (this.disableClick(event)) return;

  this.setMousePosition(event);

  if (this.isMeasurementSelected) {
    this.hideMeasurementDetails();
    this.deselectSidebarDistances();
  }

  let intersects = this.raycaster.intersectObject(this.modelObject.children[0]);
  if (intersects.length < 1) return;
  this.disableDragControls();

  let o = intersects[0];

  let pIntersect = o.point.clone();
  this.scene.worldToLocal(pIntersect);

  if (this.measurements.length) {
    let pointIntersects = this.raycaster.intersectObject(
      this.measurements[this.measurements.length - 1].firstPoint
    );

    if (pointIntersects.length > 0) {
      return;
    }
  }

  const marker = this.createMeasurementMarker(pIntersect);

  if (this.dragOn) return;

  // first point placement
  if (
    this.measurements.length === 0 ||
    this.measurements[this.measurements.length - 1].secondPoint
  ) {
    this.measurements.push({ firstPoint: marker });

    let currentMeasurement = this.measurements[this.measurements.length - 1];

    let tempMarker = this.createMeasurementMarker(pIntersect);
    currentMeasurement.tempPoint = tempMarker;

    this.camera.lookAt(currentMeasurement.firstPoint.position);
    const firstPoint = new THREE.Vector3();
    const secondPoint = new THREE.Vector3();

    currentMeasurement.firstPoint.getWorldPosition(firstPoint);
    currentMeasurement.tempPoint.getWorldPosition(secondPoint);

    let points = [firstPoint, secondPoint];

    this.tempLine = this.createReactiveThickLine(points, 4.0, true);
    const line = this.tempLine;
    // add label on line
    let label = this.createLabelBetweenTwoPoints(
      firstPoint,
      secondPoint,
      0.5,
      true,
      line
    );

    const {
      line: xAxisLine,
      label: xAxisLabel,
      distance: verticalComponent,
    } = this.createCoordinateMeasurementLine(
      firstPoint,
      new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
      COORDINTATE_LINE_COLOR_X,
      true
    );
    currentMeasurement.verticalComponent = verticalComponent.toFixed(2);

    const {
      line: yAxisLine,
      label: yAxisLabel,
      distance: horizontalComponent,
    } = this.createCoordinateMeasurementLine(
      new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
      new THREE.Vector3(secondPoint.x, secondPoint.y, secondPoint.z),
      COORDINTATE_LINE_COLOR_Y,
      false
    );
    currentMeasurement.horizontalComponent = horizontalComponent.toFixed(2);

    this.measurements[this.measurements.length - 1].tempLine = line;
    this.measurements[this.measurements.length - 1].tempLabel = label;
    this.measurements[this.measurements.length - 1].xAxisLine = xAxisLine;
    this.measurements[this.measurements.length - 1].yAxisLine = yAxisLine;
    this.measurements[this.measurements.length - 1].xAxisLabel = xAxisLabel;
    this.measurements[this.measurements.length - 1].yAxisLabel = yAxisLabel;

    // add point undo callback function
    this.undoStack.push({
      action: "ADD_MEASUREMENT_POINT",
      measurement: currentMeasurement,
    });
    this.resetRedoStack();

    // second point placement
  } else {
    let currentMeasurement = this.measurements[this.measurements.length - 1];

    currentMeasurement.secondPoint = marker;

    const firstPoint = new THREE.Vector3();
    const secondPoint = new THREE.Vector3();

    currentMeasurement.firstPoint.getWorldPosition(firstPoint);
    currentMeasurement.secondPoint.getWorldPosition(secondPoint);

    let points = [firstPoint, secondPoint];

    this.removeObjectFromScene(this.tempLine);

    let line = this.createReactiveThickLine(points, 4.0);
    this.measurementLine = line;

    this.camera.lookAt(currentMeasurement.firstPoint.position);

    let label = this.createLabelBetweenTwoPoints(
      firstPoint,
      secondPoint,
      0.5,
      true,
      line
    );

    this.removeObjectFromScene(currentMeasurement.tempLine);
    this.removeObjectFromScene(currentMeasurement.tempLabel);

    this.scene.add(line);
    this.scene.add(label);

    currentMeasurement.line = line;
    currentMeasurement.label = label;
    currentMeasurement.distance = label.element.distance;

    if (!this.anonymousUser) {
      try {
        const { data } = await this.createMeasurementObject([
          firstPoint,
          secondPoint,
        ]);
        currentMeasurement.id = data;
      } catch (e) {}
    }
    // removed due to mk3 split release
    this.distanceMeasurementActivate();
    this.showMeasurementDetails(currentMeasurement, true, false, false);
    // this.deselectSidebarDistances();
    this.resetUndoStack();
    this.resetRedoStack();
  }

  this.markers.push(marker);
  this.additionalMarkers.push(marker);

  if (this.isMeasurementSelected) {
    this.disableDragControls();
    this.enableDragControls();
  }
  this.scene.add(marker);
};

export const detectmeasurementIntersectionForCursorChange = function (
  event,
  persist = false
) {
  this.setMousePosition(event);
  const measurementObjects = [];

  this.measurements.forEach((measurement) => {
    measurementObjects.push(measurement.line);
  });

  let intersects = this.raycaster.intersectObjects(measurementObjects);

  if (intersects.length > 0) {
    setTimeout(() => {
      this.changeCursorToPointer();
    }, 0);
  }
};

export const detectMeasurementIntersection = function (event, persist = false) {
  if (this.dragOn) return;

  // Check if all measurements are  hidden
  const measurementsVisible =
    this.$store?.state?.section?.config?.distances_visible;
  if (!measurementsVisible && !this.anonymousUser) {
    // If all measurements are  hidden, don't detect intersections
    return [];
  }

  this.setMousePosition(event);
  const measurementObjects = [];
  this.measurements
    .filter((measurement) => measurement.line && measurement.show !== false)
    .forEach((measurement) => {
      measurementObjects.push(measurement.line);
    });

  let intersects = this.raycaster.intersectObjects(measurementObjects);

  if (intersects.length > 0) {
    const line = intersects[0].object;
    const measurement = this.measurements.find(
      (item) => item.line.id === line.id
    );

    this.showMeasurementDetails(measurement, persist);
    this.temporaryMeasurement = measurement;

    setTimeout(() => {
      this.changeCursorToPointer();
    }, 0);
    if (persist) this.measurementDetailsPersistent = true;
  } else {
    if (!this.measurementDetailsPersistent) {
      this.hideMeasurementDetails();
      this.restoreDefaultCursor();
    } else if (persist) {
      if (this.measurementLabelClicked) {
        this.measurementLabelClicked = false;
        return;
      }
      this.deselectSidebarDistances();
      this.hideMeasurementDetails();
      this.measurementDetailsPersistent = false;
    } else if (
      this.selectedMeasurement &&
      this.temporaryMeasurement &&
      this.selectedMeasurement.id !== this.temporaryMeasurement.id
    ) {
      let mousemove = false;
      if (event.movementX !== 0 || event.movementY !== 0) {
        mousemove = true;
      }
      this.hideMeasurementDetails([this.temporaryMeasurement], mousemove);
      this.restoreDefaultCursor();
    }
  }
  return intersects;
};

export const holdDistanceMeasurement = function () {
  this.cleanNavigationSetup();
  this.disableDefaultNavigation();

  document.removeEventListener(
    "click",
    this.detectPersistentMeasurementIntersection,
    false
  );

  if (this.active === 1)
    document.removeEventListener("click", this.addMarker, false);
};

export const unholdDistanceMeasurement = function () {
  this.restoreDefaultNavigation();

  setTimeout(() => {
    document.addEventListener(
      "click",
      this.detectPersistentMeasurementIntersection,
      false
    );
    if (this.active === 1)
      document.addEventListener("click", this.addMarker, false);

    // this.showMeasurement(measurement.line.id, false);
    // measurement is not defined here not sure why this was added
  }, 0);
};

export const detectPersistentMeasurementIntersection = function (event) {
  this.detectMeasurementIntersection(event, true);
};

export const inDistanceCreationMode = function () {
  if (this.measurements.length === 0) return false;

  const lastMeasurement = this.measurements[this.measurements.length - 1];

  if (lastMeasurement.secondPoint) return false;

  return true;
};

export const detectMeasurementIntesectionForCursorChange = function (event) {
  if (this.inDistanceCreationMode()) return this.changeCursorToCrosshair();

  this.setMousePosition(event);
  const measurementObjects = [];
  this.measurements
    .filter(
      (measurement) =>
        measurement.line && measurement.line.visible && measurement.secondPoint
    )
    .forEach((measurement) => {
      measurementObjects.push(measurement.line);
      measurementObjects.push(measurement.firstPoint);
      measurementObjects.push(measurement.secondPoint);
    });
  if (measurementObjects.length === 0) return false;

  let intersects = this.raycaster.intersectObjects(measurementObjects);
  if (intersects.length === 0) {
    return this.changeCursorToCrosshair();
  }

  this.changeCursorToPointer();
};

export const updatePreliminaryPointPosition = function (event) {
  if (this.dragOn) return;

  if (this.shouldThrottle()) return;

  if (
    this.measurements.length > 0 &&
    !this.measurements[this.measurements.length - 1].secondPoint
  ) {
    this.setMousePosition(event);

    const mouseData = { x: this.mouse.x, y: this.mouse.y };
    const cameraData = {
      matrixWorld: this.camera.matrixWorld.toArray(),
      projectionMatrix: this.camera.projectionMatrix.toArray(),
    };

    if (this.pendingLineRaycastId) {
      this.lineWorker.postMessage({
        type: "CANCEL_REQUEST",
        requestId: this.pendingLineRaycastId,
      });
    }

    this.pendingLineRaycastId = Date.now();

    this.lineWorker.postMessage({
      type: "RAYCAST_REQUEST",
      requestId: this.pendingLineRaycastId,
      data: {
        mouse: mouseData,
        cameraData,
      },
    });
  }
};

export const handleDistanceMeasurementLineWorkerResults = function (
  intersectionPoint,
  requestId
) {
  if (requestId !== this.pendingLineRaycastId) {
    return;
  }

  this.pendingLineRaycastId = null;

  if (!intersectionPoint) return;

  let pIntersect = new THREE.Vector3(
    intersectionPoint.x,
    intersectionPoint.y,
    intersectionPoint.z
  );

  this.scene.worldToLocal(pIntersect);

  const measurement = this.measurements[this.measurements.length - 1];

  let marker = measurement.tempPoint;
  marker.position.x = pIntersect.x;
  marker.position.y = pIntersect.y;
  marker.position.z = pIntersect.z;

  const firstPoint = new THREE.Vector3();
  const secondPoint = new THREE.Vector3();

  measurement.firstPoint.getWorldPosition(firstPoint);
  measurement.tempPoint.getWorldPosition(secondPoint);

  let points = [firstPoint, secondPoint];

  if (this.tempLine) {
    this.updateLinePosition(this.tempLine, points);
  }

  if (measurement.secondPoint) {
    return this.removeObjectFromScene(this.tempLine);
  }

  const line = this.tempLine;
  const label = measurement.tempLabel;

  const distance = this.updateLabelBetweenTwoPoints(
    label,
    firstPoint,
    secondPoint,
    0.5,
    false
  );

  const xAxisLine = measurement.xAxisLine;
  const xAxisLabel = measurement.xAxisLabel;

  const yAxisLine = measurement.yAxisLine;
  const yAxisLabel = measurement.yAxisLabel;

  this.updateCoordinateMeasurementLine(
    xAxisLine,
    xAxisLabel,
    firstPoint,
    new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z)
  );

  this.updateCoordinateMeasurementLine(
    yAxisLine,
    yAxisLabel,
    new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
    new THREE.Vector3(secondPoint.x, secondPoint.y, secondPoint.z)
  );

  const object = this.scene.getObjectById(line.id);

  // only show measurement details when the second point is at a certain distance from the first point
  if (!object && distance > 0.5) {
    this.scene.add(toRaw(label));
    this.scene.add(toRaw(line));
    this.scene.add(toRaw(xAxisLine));
    this.scene.add(toRaw(xAxisLabel));
    this.scene.add(toRaw(yAxisLine));
    this.scene.add(toRaw(yAxisLabel));
  } else if (object) {
    // only check if auxiliary label overlaps with the first point if it's not already hidden
    if (!this.checkAuxiliaryOverlapWithMainLabel(label, xAxisLabel)) {
      this.checkLabelOverlapWithFirstPoint(xAxisLabel, measurement.firstPoint);
    }

    if (!this.checkAuxiliaryOverlapWithMainLabel(label, yAxisLabel)) {
      this.checkLabelOverlapWithFirstPoint(yAxisLabel, measurement.firstPoint);
    }

    this.checkLabelOverlapWithFirstPoint(label, measurement.firstPoint);
  }
};

export const calculateCoordinatePoints = function (firstPoint, secondPoint) {
  let startPoint, endPoint;
  if (secondPoint.y < firstPoint.y) {
    startPoint = firstPoint;
    endPoint = secondPoint;
  } else {
    startPoint = secondPoint;
    endPoint = firstPoint;
  }

  const junctionPoint = new THREE.Vector3(
    startPoint.x,
    startPoint.y,
    startPoint.z
  );
  return { startPoint, endPoint, junctionPoint };
};

export const checkLabelOverlapWithFirstPoint = function (label, point) {
  const labelScreenPosition = this.getScreenPosition(label);
  const pointScreenPosition = this.getScreenPosition(point);

  const labelWidth = label.element.offsetWidth;
  const labelHeight = label.element.offsetHeight;

  const labelRect = {
    left: labelScreenPosition.x - labelWidth / 2,
    right: labelScreenPosition.x + labelWidth / 2,
    top: labelScreenPosition.y - labelHeight / 2,
    bottom: labelScreenPosition.y + labelHeight / 2,
  };

  const { width: pointWidth, height: pointHeight } = this.getScreenSize(point);
  const pointRect = {
    left: pointScreenPosition.x - pointWidth / 2,
    right: pointScreenPosition.x + pointWidth / 2,
    top: pointScreenPosition.y - pointHeight / 2,
    bottom: pointScreenPosition.y + pointHeight / 2,
  };

  if (this.checkOverlap2(labelRect, pointRect)) {
    label.element.style.visibility = "hidden";
  } else {
    label.element.style.visibility = "visible";
  }
};

export const detectAuxiliaryMeasurementIntersection = function () {
  const expandedMeasurements = this.measurements.filter(
    (measurement) => measurement.xAxisLabel.visible
  );
  for (let measurement of expandedMeasurements) {
    if (
      !this.checkAuxiliaryOverlapWithMainLabel(
        measurement.label,
        measurement.xAxisLabel
      )
    ) {
      this.checkLabelOverlapWithPoint(
        measurement.xAxisLabel,
        measurement.firstPoint
      );
    }

    if (
      !this.checkAuxiliaryOverlapWithMainLabel(
        measurement.label,
        measurement.yAxisLabel
      )
    ) {
      this.checkLabelOverlapWithPoint(
        measurement.yAxisLabel,
        measurement.firstPoint
      );
    }
  }
};

export const checkAuxiliaryOverlapWithMainLabel = function (
  mainLabel,
  secondaryLabel
) {
  const mainLabelRect = mainLabel.element.getBoundingClientRect();
  const secondaryLabelRect = secondaryLabel.element.getBoundingClientRect();

  if (this.checkOverlap(mainLabelRect, secondaryLabelRect)) {
    secondaryLabel.element.style.visibility = "hidden";
    return true;
  } else {
    secondaryLabel.element.style.visibility = "visible";
    return false;
  }
};

export const checkLabelOverlapWithPoint = function (label, point) {
  const labelScreenPosition = this.getScreenPosition(label);
  const pointScreenPosition = this.getScreenPosition(point);

  const labelWidth = label.element.offsetWidth;
  const labelHeight = label.element.offsetHeight;

  const labelRect = {
    left: labelScreenPosition.x - labelWidth / 2,
    right: labelScreenPosition.x + labelWidth / 2,
    top: labelScreenPosition.y - labelHeight / 2,
    bottom: labelScreenPosition.y + labelHeight / 2,
  };

  const { width: pointWidth, height: pointHeight } = this.getScreenSize(point);
  const pointRect = {
    left: pointScreenPosition.x - pointWidth / 2,
    right: pointScreenPosition.x + pointWidth / 2,
    top: pointScreenPosition.y - pointHeight / 2,
    bottom: pointScreenPosition.y + pointHeight / 2,
  };

  if (this.checkOverlap2(labelRect, pointRect)) {
    label.element.style.visibility = "hidden";
  } else {
    label.element.style.visibility = "visible";
  }
};

export const getScreenSize = function (object) {
  const bbox = new THREE.Box3().setFromObject(object);

  const size = new THREE.Vector3();
  bbox.getSize(size);

  const widthHalf = 0.5 * this.renderer.domElement.width;
  const heightHalf = 0.5 * this.renderer.domElement.height;

  const corners = [
    new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.min.z),
    new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.max.z),
    new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.min.z),
    new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.max.z),
    new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.min.z),
    new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.max.z),
    new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.min.z),
    new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.max.z),
  ];

  const min = new THREE.Vector3(Infinity, Infinity, Infinity);
  const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

  corners.forEach((corner) => {
    corner.project(this.camera);
    corner.x = corner.x * widthHalf + widthHalf;
    corner.y = -(corner.y * heightHalf) + heightHalf;

    min.min(corner);
    max.max(corner);
  });

  return {
    width: max.x - min.x,
    height: max.y - min.y,
  };
};

export const checkOverlap = function (rect1, rect2) {
  return (
    rect1.x < rect2.x + rect2.width &&
    rect1.x + rect1.width > rect2.x &&
    rect1.y < rect2.y + rect2.height &&
    rect1.y + rect1.height > rect2.y
  );
};

export const checkOverlap2 = function (rect1, rect2) {
  return !(
    rect1.right < rect2.left ||
    rect1.left > rect2.right ||
    rect1.bottom < rect2.top ||
    rect1.top > rect2.bottom
  );
};

export const getScreenPosition = function (object) {
  const vector = new THREE.Vector3();
  const widthHalf = 0.5 * this.renderer.domElement.width;
  const heightHalf = 0.5 * this.renderer.domElement.height;

  object.updateMatrixWorld();
  vector.setFromMatrixPosition(object.matrixWorld);
  vector.project(this.camera);

  vector.x = vector.x * widthHalf + widthHalf;
  vector.y = -(vector.y * heightHalf) + heightHalf;

  return {
    x: vector.x,
    y: vector.y,
  };
};

export const enableDragControls = function () {
  document.addEventListener("mousedown", this.dragMarkerStart);
};

export const disableDragControls = function () {
  document.removeEventListener("mousedown", this.dragMarkerStart);
  document.removeEventListener("mousemove", this.dragMarker);
  document.removeEventListener("mouseup", this.dragMarkerEnd);
};

export const showMeasurementDetails = function (
  measurement,
  showRemoveIcon = false,
  fromSidebar = false,
  resetMode = true
) {
  if (showRemoveIcon) {
    if (this.active !== 0 && this.active !== 1 && !fromSidebar) return;
    if (this.active !== 1 && resetMode) {
      this.toggleActive(0);
    }
    let filteredMeasurements = this.measurements.filter((element) => {
      return element.label?.id !== measurement?.label?.id;
    });
    const foundIndex = this.measurements.findIndex((element) => {
      return element.label?.id === measurement?.label?.id;
    });
    if (!fromSidebar) {
      this.unselectArea();

      if (
        !this.anonymousUser &&
        (!this.sample || this.project.isSampleProject)
      ) {
        this.selectMeasurementFromCanvas(foundIndex);
      }
    }
    if (filteredMeasurements.length > 0) {
      this.selectedMeasurement = measurement;
      this.hideMeasurementDetails(filteredMeasurements);
    }
    this.isMeasurementSelected = true;
  }

  if (!measurement) return;

  const xAxisLine = measurement.xAxisLine;
  const yAxisLine = measurement.yAxisLine;
  const xAxisLabel = measurement.xAxisLabel;
  const yAxisLabel = measurement.yAxisLabel;
  const firstPoint = measurement.firstPoint;
  const label = measurement.label;

  const xLineObject = this.scene.getObjectById(xAxisLine.id);
  const firstPointObject = this.scene.getObjectById(firstPoint.id);

  const xLabelObject = this.scene.getObjectById(xAxisLabel.id);
  const yLabelObject = this.scene.getObjectById(yAxisLabel.id);

  if (firstPointObject.visible) {
    if (xLineObject) {
      const yLineObject = this.scene.getObjectById(yAxisLine.id);

      xLineObject.visible = true;
      yLineObject.visible = true;

      xLabelObject.visible = true;
      yLabelObject.visible = true;
    } else {
      this.scene.add(
        toRaw(xAxisLine),
        toRaw(yAxisLine),
        toRaw(xAxisLabel),
        toRaw(yAxisLabel)
      );
    }
    if (showRemoveIcon) {
      label.element.children[1].style.display = "inline";
      this.setPointColor(measurement.firstPoint, viewerColors.SELECTED_LINE_COLOR);
      this.setPointColor(measurement.secondPoint, viewerColors.SELECTED_LINE_COLOR);
      this.setLineColor(measurement.line, viewerColors.SELECTED_LINE_COLOR);
    }
  }
};

export const hideMeasurementDetails = function (
  filteredMeasurements,
  hover = false
) {
  const measurementsToHide = filteredMeasurements || this.measurements;

  if (this.isMeasurementSelected && !hover) {
    this.isMeasurementSelected = false;
  }

  for (let measurement of measurementsToHide) {
    const xAxisLine = measurement.xAxisLine;
    const yAxisLine = measurement.yAxisLine;
    const xAxisLabel = measurement.xAxisLabel;
    const yAxisLabel = measurement.yAxisLabel;
    const label = measurement.label;

    const xLineObject = this.scene.getObjectById(xAxisLine.id);
    if (xLineObject) {
      const yLineObject = this.scene.getObjectById(yAxisLine.id);
      const xLabelObject = this.scene.getObjectById(xAxisLabel.id);
      const yLabelObject = this.scene.getObjectById(yAxisLabel.id);

      xLineObject.visible = false;
      yLineObject.visible = false;
      xLabelObject.visible = false;
      yLabelObject.visible = false;
      if (label) {
        label.element.children[1].style.display = "none";
        label.element.className = "measurementLabel";
      }

      if (measurement.firstPoint)
        this.setPointColor(measurement.firstPoint, viewerColors.POINT_COLOR);
      if (measurement.secondPoint)
        this.setPointColor(measurement.secondPoint, viewerColors.POINT_COLOR);
      if (measurement.line) this.setLineColor(measurement.line, viewerColors.POINT_COLOR);
    }
  }
};

export const showMeasurement = function (lineId, resetMode = true) {
  if (this.active !== 1 && this.active !== 0) {
    return;
  }
  const measurement = this.measurements.find(
    (measurement) => measurement.line.id == lineId
  );

  this.showMeasurementDetails(measurement, true, false, resetMode);
  this.measurementDetailsPersistent = true;
  this.measurementLabelClicked = true;
};
export const showMeasurementFromSidebar = function (lineId) {
  if (lineId) {
    const fromSidebar = true;
    const measurement = this.measurements.find(
      (measurement) => measurement.line.id == lineId
    );
    this.showMeasurementDetails(measurement, true, fromSidebar);
    this.measurementDetailsPersistent = true;
    this.measurementLabelClicked = true;
  } else {
    this.hideMeasurementDetails(this.measurements);
  }
};

export const deleteMeasurement = function (lineId) {
  const measurement = this.measurements.find(
    (measurement) => measurement.line.id == lineId
  );

  this.removeObjectFromScene(measurement.line);
  this.removeObjectFromScene(measurement.label);
  this.removeObjectFromScene(measurement.firstPoint);
  this.removeObjectFromScene(measurement.secondPoint);
  this.removeObjectFromScene(measurement.xAxisLine);
  this.removeObjectFromScene(measurement.xAxisLabel);
  this.removeObjectFromScene(measurement.yAxisLine);
  this.removeObjectFromScene(measurement.yAxisLabel);

  this.deleteMeasurementObject(measurement.id);

  this.measurements = this.measurements.filter(
    (m) => m.line.id !== measurement.line.id
  );

  this.undoStack.push({
    action: "DELETE_MEASUREMENT",
    measurement: {
      position: [
        {
          x: measurement.firstPoint.position.x,
          y: measurement.firstPoint.position.y,
          z: measurement.firstPoint.position.z,
        },
        {
          x: measurement.secondPoint.position.x,
          y: measurement.secondPoint.position.y,
          z: measurement.secondPoint.position.z,
        },
      ],
      show: true,
    },
  });
  this.resetRedoStack();
};

export const dragMarkerStart = function (event) {
  this.setMousePosition(event);

  const closedMeasurements = this.measurements.filter(
    (measurement) => measurement.secondPoint && measurement.show !== false
  );
  const points = closedMeasurements.flatMap((measurement) => [
    measurement.firstPoint,
    measurement.secondPoint,
  ]);

  const pointIntersects = this.raycaster.intersectObjects(points, true);

  if (pointIntersects.length === 0) return;

  let draggedMarker = pointIntersects[0].object;
  let measurement = this.measurements.find(
    (m) =>
      m.firstPoint.id === draggedMarker.id ||
      m.secondPoint.id === draggedMarker.id
  );
  let isFirstPoint = measurement.firstPoint.id === draggedMarker.id;

  if (!measurement) return;

  if (
    this.existingMeasurementsIds.includes(measurement.id) &&
    this.anonymousUser
  ) {
    return;
  }

  this.holdDistanceMeasurement();
  // this is used to prevent selecting area measurement after dragging distance measurement
  this.measurementDrag = true;

  this.draggedMeasurementPoint = {
    measurement,
    isFirstPoint,
    draggedMarker,
  };

  draggedMarker.prevPosition = {
    x: draggedMarker.position.x,
    y: draggedMarker.position.y,
    z: draggedMarker.position.z,
  };
  draggedMarker.lastPosition = {
    x: draggedMarker.position.x,
    y: draggedMarker.position.y,
    z: draggedMarker.position.z,
  };

  this.showMeasurement(measurement.line.id, false);

  this.dragOn = true;

  document.addEventListener("mousemove", this.dragMarker);
  document.addEventListener("mouseup", this.dragMarkerEnd);
};

export const dragMarker = function (event) {
  if (this.shouldThrottle()) return;

  if (!this.dragOn) return;

  const draggedMarker = this.draggedMeasurementPoint.draggedMarker;

  this.setMousePosition(event);

  const mouseData = { x: this.mouse.x, y: this.mouse.y };
  const cameraData = {
    matrixWorld: this.camera.matrixWorld.toArray(),
    projectionMatrix: this.camera.projectionMatrix.toArray(),
  };

  if (this.pendingLineRaycastId) {
    this.lineWorker.postMessage({
      type: "CANCEL_REQUEST",
      requestId: this.pendingLineRaycastId,
    });
  }

  this.pendingLineRaycastId = Date.now();

  this.lineWorker.postMessage({
    type: "RAYCAST_REQUEST",
    requestId: this.pendingLineRaycastId,
    data: {
      mouse: mouseData,
      cameraData,
    },
    draggedMarkerId: draggedMarker.id,
  });
};

export const dragMarkerEnd = function () {
  const { measurement, draggedMarker } = this.draggedMeasurementPoint;

  this.unholdDistanceMeasurement(measurement);

  this.updateMeasurementObject(measurement.id, [
    {
      x: measurement.firstPoint.position.x,
      y: measurement.firstPoint.position.y,
      z: measurement.firstPoint.position.z,
    },
    {
      x: measurement.secondPoint.position.x,
      y: measurement.secondPoint.position.y,
      z: measurement.secondPoint.position.z,
    },
  ]);

  this.undoStack.push({
    action: "MOVE_MEASUREMENT_POINT",
    measurement: measurement,
    point: draggedMarker,
  });
  setTimeout(() => {
    this.measurementDrag = false;
    this.dragOn = false;
    this.draggedMeasurementPoint = null;

    document.removeEventListener("mousemove", this.dragMarker);
    document.removeEventListener("mouseup", this.dragMarkerEnd);
  }, 0);
  this.resetRedoStack();
};

export const reDraw = function (measurement) {
  const firstPoint = new THREE.Vector3();
  const secondPoint = new THREE.Vector3();

  measurement.firstPoint.getWorldPosition(firstPoint);
  measurement.secondPoint.getWorldPosition(secondPoint);

  let points = [firstPoint, secondPoint];

  this.updateLinePosition(measurement.line, points);

  this.updateLabelBetweenTwoPoints(
    measurement.label,
    firstPoint,
    secondPoint,
    0.5,
    false,
    true,
    measurement.line
  );
  measurement.distance = measurement.label.element.distance;

  const xAxisLine = measurement.xAxisLine;
  const xAxisLabel = measurement.xAxisLabel;

  const yAxisLine = measurement.yAxisLine;
  const yAxisLabel = measurement.yAxisLabel;

  this.updateCoordinateMeasurementLine(
    xAxisLine,
    xAxisLabel,
    firstPoint,
    new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z)
  );

  this.updateCoordinateMeasurementLine(
    yAxisLine,
    yAxisLabel,
    new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
    new THREE.Vector3(secondPoint.x, secondPoint.y, secondPoint.z)
  );

  if (!this.checkAuxiliaryOverlapWithMainLabel(measurement.label, xAxisLabel)) {
    this.checkLabelOverlapWithFirstPoint(xAxisLabel, measurement.firstPoint);
  }

  if (!this.checkAuxiliaryOverlapWithMainLabel(measurement.label, yAxisLabel)) {
    this.checkLabelOverlapWithFirstPoint(yAxisLabel, measurement.firstPoint);
  }

  this.checkLabelOverlapWithFirstPoint(
    measurement.label,
    measurement.firstPoint
  );
};

export const handleDragDistanceMeasurementLineWorkerResults = function (
  intersectionPoint,
  requestId
) {
  if (requestId !== this.pendingLineRaycastId) {
    return;
  }

  this.pendingLineRaycastId = null;

  if (!intersectionPoint || !this.draggedMeasurementPoint?.draggedMarker)
    return;

  let pIntersect = new THREE.Vector3(
    intersectionPoint.x,
    intersectionPoint.y,
    intersectionPoint.z
  );

  this.scene.worldToLocal(pIntersect);

  const { draggedMarker, measurement } = this.draggedMeasurementPoint;

  draggedMarker.intersectionPoint = intersectionPoint;

  draggedMarker.position.x = intersectionPoint.x;
  draggedMarker.position.y = intersectionPoint.y;
  draggedMarker.position.z = intersectionPoint.z;

  draggedMarker.prevPosition = {
    x: draggedMarker.position.x,
    y: draggedMarker.position.y,
    z: draggedMarker.position.z,
  };

  this.reDraw(measurement);
};

export const displayMarker = function (measurement, point, fromUndo = false) {
  const marker = this.createMeasurementMarker(point);

  const measurementId = measurement.id;
  // first point placement
  if (
    this.measurements.length === 0 ||
    this.measurements[this.measurements.length - 1].secondPoint
  ) {
    this.measurements.push({ id: measurementId, firstPoint: marker });
    // second point placement
  } else {
    let currentMeasurement = this.measurements[this.measurements.length - 1];

    currentMeasurement.secondPoint = marker;

    const firstPoint = new THREE.Vector3();
    const secondPoint = new THREE.Vector3();

    currentMeasurement.firstPoint.getWorldPosition(firstPoint);
    currentMeasurement.secondPoint.getWorldPosition(secondPoint);

    let points = [firstPoint, secondPoint];
    let line = this.createReactiveThickLine(points, 4.0);
    let label = this.createLabelBetweenTwoPoints(
      firstPoint,
      secondPoint,
      0.5,
      true,
      line
    );
    this.scene.add(line);
    this.scene.add(label);

    currentMeasurement.line = line;
    currentMeasurement.show = measurement.show;
    currentMeasurement.label = label;
    currentMeasurement.distance = label.element.distance;

    const {
      line: xAxisLine,
      label: xAxisLabel,
      distance: verticalComponent,
    } = this.createCoordinateMeasurementLine(
      firstPoint,
      new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
      COORDINTATE_LINE_COLOR_X,
      true
    );
    currentMeasurement.verticalComponent = verticalComponent.toFixed(2);

    const {
      line: yAxisLine,
      label: yAxisLabel,
      distance: horizontalComponent,
    } = this.createCoordinateMeasurementLine(
      new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
      new THREE.Vector3(secondPoint.x, secondPoint.y, secondPoint.z),
      COORDINTATE_LINE_COLOR_Y,
      false
    );
    currentMeasurement.horizontalComponent = horizontalComponent.toFixed(2);

    this.scene.add(xAxisLine);
    this.scene.add(xAxisLabel);
    this.scene.add(yAxisLine);
    this.scene.add(yAxisLabel);

    currentMeasurement.xAxisLine = xAxisLine;
    currentMeasurement.yAxisLine = yAxisLine;
    currentMeasurement.xAxisLabel = xAxisLabel;
    currentMeasurement.yAxisLabel = yAxisLabel;

    if (currentMeasurement.show === false) {
      hideSelectedMeasurement(currentMeasurement);
    }

    if (!fromUndo) this.toggleActive(0);
  }
  this.markers.push(marker);

  if (!this.anonymousUser) {
    this.disableDragControls();
    this.enableDragControls();
  }

  this.scene.add(marker);
};
export const removeUnfinishedMeasurements = function () {
  const measurement = this.measurements[this.measurements.length - 1];
  if (measurement && !measurement.secondPoint) {
    const firstPoint = measurement.firstPoint;
    const tempLine = measurement.tempLine;
    const tempLabel = measurement.tempLabel;
    const xAxisLine = measurement.xAxisLine;
    const yAxisLine = measurement.yAxisLine;
    const xAxisLabel = measurement.xAxisLabel;
    const yAxisLabel = measurement.yAxisLabel;

    const firstPointObject = this.scene.getObjectById(firstPoint.id);
    const tempLineObject = this.scene.getObjectById(tempLine.id);
    const tempLabelObject = this.scene.getObjectById(tempLabel.id);
    const xAxisLineObject = this.scene.getObjectById(xAxisLine.id);
    const yAxisLineObject = this.scene.getObjectById(yAxisLine.id);
    const xAxisLabelObject = this.scene.getObjectById(xAxisLabel.id);
    const yAxisLabelObject = this.scene.getObjectById(yAxisLabel.id);

    this.scene.remove(
      firstPointObject,
      tempLineObject,
      tempLabelObject,
      xAxisLineObject,
      yAxisLineObject,
      xAxisLabelObject,
      yAxisLabelObject
    );

    this.measurements.pop();
  }
  this.disableDragControls();
  this.enableDragControls();
};

export const removeMeasurements = function () {
  if (this.measurements.length > 0) {
    this.measurements.forEach((measurement) => {
      this.deleteMeasurement(measurement.line.id);
    });
    this.measurements = [];
  }
};

export const hideMeasurements = function () {
  if (this.measurements.length > 0) {
    this.measurements.forEach((measurement) => {
      hideSelectedMeasurement(measurement);
    });
  }
};

export const showMeasurements = function () {
  if (this.measurements.length > 0) {
    this.measurements.forEach((measurement) => {
      if (measurement.show) {
        showSelectedMeasurement(measurement);
      }
    });
  }
};

export const hideSelectedMeasurement = function (selectedMeasurement) {
  // Hide the first point
  if (selectedMeasurement.firstPoint) {
    selectedMeasurement.firstPoint.visible = false;
  }

  // Hide the second point
  if (selectedMeasurement.secondPoint) {
    selectedMeasurement.secondPoint.visible = false;
  }

  // Hide the line
  if (selectedMeasurement.line) {
    selectedMeasurement.line.visible = false;
  }

  // Hide the label
  if (selectedMeasurement.label) {
    selectedMeasurement.label.visible = false;
  }
  if (selectedMeasurement.xAxisLabel) {
    selectedMeasurement.xAxisLabel.visible = false;
  }
  if (selectedMeasurement.yAxisLabel) {
    selectedMeasurement.yAxisLabel.visible = false;
  }
  if (selectedMeasurement.xAxisLine) {
    selectedMeasurement.xAxisLine.visible = false;
  }
  if (selectedMeasurement.yAxisLine) {
    selectedMeasurement.yAxisLine.visible = false;
  }
};
export const showSelectedMeasurement = function (selectedMeasurement) {
  // Show the first point
  if (selectedMeasurement.firstPoint) {
    selectedMeasurement.firstPoint.visible = true;
  }

  // Show the second point
  if (selectedMeasurement.secondPoint) {
    selectedMeasurement.secondPoint.visible = true;
  }

  // Show the line
  if (selectedMeasurement.line) {
    selectedMeasurement.line.visible = true;
  }

  // Show the label
  if (selectedMeasurement.label) {
    selectedMeasurement.label.visible = true;
  }
};

export const createMeasurementObject = async function (points) {
  if (this.sample) return;
  const measurementObject = {
    projectId: Number(this.projectId),
    position: points,
  };
  return await API.airteam3DViewer.createMeasurementObject(measurementObject);
};

export const updateMeasurementObject = async function (id, points) {
  if (this.sample || this.anonymousUser) return;
  const measurementObject = {
    id,
    projectId: Number(this.projectId),
    position: points,
  };
  return await API.airteam3DViewer.updateMeasurementObject(measurementObject);
};

export const deleteMeasurementObject = async function (id) {
  if (this.sample || this.anonymousUser) return;
  return await API.airteam3DViewer.deleteObject(id);
};

export const reAddMarker = function () {
  document.removeEventListener("click", this.reAddMarker, false);
  document.addEventListener("click", this.addMarker, false);
};

export const undoAddMeasurementPoint = function (measurement) {
  this.redoStack.push({
    action: "ADD_MEASUREMENT_POINT",
    measurement,
  });

  const point = measurement.firstPoint;
  const tempLine = measurement.tempLine;
  const tempLabel = measurement.tempLabel;
  const xAxisLine = measurement.xAxisLine;
  const yAxisLine = measurement.yAxisLine;
  const xAxisLabel = measurement.xAxisLabel;
  const yAxisLabel = measurement.yAxisLabel;

  this.markers = this.markers.filter((marker) => marker.id !== point.id);
  this.measurements = this.measurements.slice(0, this.measurements.length - 1);

  this.hideObjectFromScene(point);
  if (tempLine) this.hideObjectFromScene(tempLine);
  if (tempLabel) this.hideObjectFromScene(tempLabel);
  if (xAxisLine) this.hideObjectFromScene(xAxisLine);
  if (xAxisLabel) this.hideObjectFromScene(yAxisLine);
  if (yAxisLine) this.hideObjectFromScene(xAxisLabel);
  if (yAxisLabel) this.hideObjectFromScene(yAxisLabel);

  this.tempLine = null;
};

export const redoAddMeasurementPoint = function (measurement) {
  const point = measurement.firstPoint;
  const tempLine = measurement.tempLine;
  const tempLabel = measurement.tempLabel;
  const xAxisLine = measurement.xAxisLine;
  const yAxisLine = measurement.yAxisLine;
  const xAxisLabel = measurement.xAxisLabel;
  const yAxisLabel = measurement.yAxisLabel;

  this.markers.push(point);
  this.measurements.push(measurement);

  point.visible = true;
  if (tempLine) tempLine.visible = true;
  if (tempLabel) tempLabel.visible = true;
  if (xAxisLine) xAxisLine.visible = true;
  if (xAxisLabel) yAxisLine.visible = true;
  if (yAxisLine) xAxisLabel.visible = true;
  if (yAxisLabel) yAxisLabel.visible = true;

  this.tempLine = tempLine;

  this.undoStack.push({
    action: "ADD_MEASUREMENT_POINT",
    measurement,
  });
};

export const removeLastDistancePoint = function (event) {
  const key = event.key;
  if (key !== "Backspace" && key !== "Delete") return;

  if (this.measurements.length === 0) return;
  this.removeUnfinishedMeasurements();
};

export const updateMeasurementLabelCursors = function (active) {
  // Get all measurement labels
  const measurementLabels = document.querySelectorAll(".measurementLabel");

  measurementLabels.forEach((label) => {
    if (active === 0 || active === 1) {
      label.style.cursor = "pointer";
    } else {
      label.style.cursor = "default"; // or 'inherit' or whatever default cursor you want
    }
  });
};
